/**
 * Croatian overrides for the fiveS screens.
 *
 * Only what genuinely differs from Bosnian. Croatian is ijekavica too, so
 * nothing moves for the jat reflex — what changes is vocabulary: *sustav*,
 * *tvrtka*, *datoteka*, *sigurnost*, *tipka*, and borrowed verbs in -irati.
 *
 * An entry identical to its Bosnian base is a bug, not a no-op: i18n.test.ts
 * fails on one, because a line that changes nothing is a decision that was
 * reverted in bs.ts without anybody revisiting this file.
 *
 * The word that moves most here is *kriterijum* — Croatian has *kriterij*,
 * and it is on almost every line of a screen made of criteria.
 */

import type { FiveSSlice } from '../bs/fiveS'

export const fiveS: Partial<FiveSSlice> = {
  // *stroj* for a machine, which is what carries the help text across.
  'fiveS.help.oneWalk':
    'Jedan obilazak po stroju, po danu, po smjeni. Jutarnji i noćni obilazak istog stroja istog dana su dva odvojena zapisa, jer opisuju dva različita stanja centra.',

  'fiveS.workCenter.placeholder': 'Odaberite',
  'fiveS.date.help':
    'Upisujete dan kad ste stvarno obišli stroj, a ne dan kad unosite ocjene — zapis opisuje stanje centra tog dana.',
  // *tko*, *razdoblje*, *obvezno*.
  'fiveS.workers.hint': 'Tko je održavao centar u ovom razdoblju. Nije obvezno.',
  // *popis* rather than *spisak*, and the machine again.
  'fiveS.workers.help':
    'Iz ovog popisa se računa 5S ocjena po radniku, a ona opisuje stanje stroja koji su držali, ne njih same.',
  // *smjena* is shared; only *obvezno* moves.
  'fiveS.shift.hint': 'Koja smjena je držala centar. Nije obvezno.',

  'fiveS.pickCenter.title': 'Odaberite radni centar',
  // *zavisiti od* → *ovisiti o*, *izbor* → *odabir*.
  'fiveS.pickCenter.description':
    'Obrazac i skala ocjenjivanja ovise o tipu centra, pa se kriteriji prikazuju tek nakon odabira.',

  // *lista* → *popis*.
  'fiveS.onlyUnanswered.hint':
    'Korisno pri kraju obilaska, da ne tražite propušteno po cijelom popisu.',

  'fiveS.remaining': 'Preostalo kriterija: {count}',
  'fiveS.allScored': 'Svi kriteriji ocijenjeni',
  'fiveS.progressLabel': 'Ocijenjeni kriteriji',
  // *postotak*, *zbroj*, *uspoređivati*.
  'fiveS.percentage.help':
    'Postotak se koristi jer radni centri mogu imati različite skale, pa se sirovi zbrojevi ne mogu uspoređivati.',

  // *spremiti* rather than *sačuvati*, everywhere a draft is involved.
  'fiveS.saveDraft': 'Spremi nacrt',
  'fiveS.submit.hint':
    'Predaja je moguća tek kada su svi kriteriji ocijenjeni. Nacrt možete spremiti bilo kada.',
  // *predana*, and the machine.
  'fiveS.submit.help':
    'Predana evaluacija se više ne mijenja jer je to zapis stanja stroja tog dana; ponovo je otvara samo administrator.',
  'fiveS.submit.blocked': 'Prije predaje ocijenite sve kriterije. Neocijenjeno: {count}.',
  'fiveS.draftSaved': 'Nacrt je spremljen.',
  // *predan*, not *predata*.
  'fiveS.submitted': 'Evaluacija je predana — {score}.',
  'fiveS.submitted.next': 'Evaluacija je predana — {score}. Odaberite sljedeći radni centar.',
  'fiveS.saveFailed': 'Spremanje nije uspjelo.',

  'fiveS.centerChange.keepHint':
    'Ako ih želite spremiti, prvo zatvorite ovaj prozor i pritisnite „Spremi nacrt”.',

  // *stroj*, and printing is *ispisivanje* rather than *štampanje*.
  'fiveS.stickers.subtitle':
    'Naljepnice za strojeve. Radnik skenira kod i prijava se otvara s već odabranim radnim centrom.',
  'fiveS.stickers.printHint':
    'Ispišite na A4, izrežite po isprekidanoj liniji i plastificirajte. Zalijepite u visini očiju, dalje od prašine i ulja.',
  'fiveS.stickers.printAll': 'Ispiši sve',
  'fiveS.stickers.printSelected': 'Ispiši ({count})',
  'fiveS.stickers.clearSelection': 'Poništi odabir',
  'fiveS.stickers.select': 'Odaberi {name}',
  'fiveS.stickers.hasCode': 'Izdan',
  'fiveS.stickers.regenerateConfirm':
    'Obnoviti QR kod za „{name}"?\n\nNaljepnica koja je trenutno na stroju odmah prestaje vrijediti. Morat ćete ispisati i zalijepiti novu.',

  // *račun* rather than *nalog*, and an ID card is an *osobna iskaznica*.
  'fiveS.badge.warning':
    'Kartica je kao ključ — tko je ima, može se prijaviti ovim računom, pa i fotografija kartice. Čuvajte je kao osobnu iskaznicu. Ako se izgubi, izdajte novu; stara odmah prestaje vrijediti.',
  'fiveS.list.readOnly': '5S evaluacije obavljaju odgovorne osobe — ovdje ih možete pregledati.',
}
